import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TEMPERATURES, fmtEuro, fmtDate } from "@/lib/crm";
import { UserAvatar } from "@/components/crm/Badges";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2, ArrowLeft, Users, PhoneCall, CalendarPlus, CalendarCheck, Ban,
  Briefcase, Trophy, XCircle, Percent, Wallet, TrendingUp, ChevronRight, AlertTriangle, Phone,
} from "lucide-react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { cn } from "@/lib/utils";

const PERIODS = [
  { k: "today", l: "Oggi" }, { k: "week", l: "Settimana" }, { k: "month", l: "Mese" },
  { k: "quarter", l: "Trimestre" }, { k: "year", l: "Anno" }, { k: "custom", l: "Personalizzato" },
  { k: "all", l: "Sempre" },
];

const TIP_STYLE = { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 };

export default function UserPerformance() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: me, isAdmin } = useAuth();
  const [period, setPeriod] = useState("month");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { period };
      if (period === "custom" && custom.start && custom.end) {
        params.start = new Date(custom.start).toISOString();
        params.end = new Date(custom.end).toISOString();
      }
      const { data } = await api.get(`/users/${userId}/performance`, { params });
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Impossibile caricare la dashboard.");
    } finally {
      setLoading(false);
    }
  }, [userId, period, custom]);

  useEffect(() => {
    if (period === "custom" && !(custom.start && custom.end)) return;
    load();
  }, [load, period, custom]);

  const canView = isAdmin || me?.id === userId;

  if (!canView) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-6 text-center max-w-md mx-auto mt-10" data-testid="perf-forbidden">
        <AlertTriangle className="w-8 h-8 text-destructive mx-auto mb-2" />
        <p className="text-sm text-destructive">Non hai i permessi per vedere queste performance.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-up">
      {/* 1. Profilo */}
      <div className="flex items-center gap-3">
        <button data-testid="perf-back" onClick={() => navigate(isAdmin ? "/team" : "/")} className="p-2 rounded-md hover:bg-accent transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        {data?.user ? (
          <div className="flex items-center gap-3">
            <UserAvatar user={data.user} size={44} />
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight leading-none">{data.user.name}</h1>
              <p className="text-sm text-muted-foreground mt-1">{data.user.email} · <span className="capitalize">{data.user.role}</span></p>
              {data.user.work_phone && (
                <a href={`tel:${data.user.work_phone}`} data-testid="perf-work-phone" className="text-sm text-primary hover:underline mt-1 inline-flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" />{data.user.work_phone}
                </a>
              )}
            </div>
          </div>
        ) : (
          <h1 className="font-heading text-2xl font-black tracking-tight">Performance</h1>
        )}
      </div>

      {/* 2. Filtro periodo */}
      <div className="space-y-3">
        <div className="flex rounded-md border overflow-hidden w-fit flex-wrap">
          {PERIODS.map((p) => (
            <button key={p.k} data-testid={`perf-period-${p.k}`} onClick={() => setPeriod(p.k)}
              className={cn("px-3 py-1.5 text-sm transition-colors", period === p.k ? "bg-primary text-primary-foreground" : "hover:bg-accent")}>
              {p.l}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex gap-2 items-end">
            <div><Label className="text-xs">Da</Label><Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="h-8" data-testid="perf-custom-start" /></div>
            <div><Label className="text-xs">A</Label><Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="h-8" data-testid="perf-custom-end" /></div>
          </div>
        )}
      </div>

      {error ? (
        <div data-testid="perf-error" className="rounded-lg border border-destructive/30 bg-destructive/10 p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-destructive mx-auto mb-2" />
          <p className="text-sm text-destructive mb-4">{error}</p>
          <button onClick={load} className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium">Riprova</button>
        </div>
      ) : loading || !data ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" data-testid="perf-loader" /></div>
      ) : (
        <Dashboard data={data} navigate={navigate} />
      )}
    </div>
  );
}

function Dashboard({ data, navigate }) {
  const k = data.kpis;
  const kpiCards = [
    { key: "assegnati", label: "Lead assegnati", value: k.assegnati, icon: Users, color: "text-sky-500" },
    { key: "lavorati", label: "Lead lavorati", value: k.lavorati, icon: PhoneCall, color: "text-blue-500", sub: k.assegnati ? `${Math.round((k.lavorati / k.assegnati) * 100)}% degli assegnati` : null },
    { key: "app_fissati", label: "App. fissati", value: k.appuntamenti_fissati, icon: CalendarPlus, color: "text-indigo-500" },
    { key: "app_effettuati", label: "App. effettuati", value: k.appuntamenti_effettuati, icon: CalendarCheck, color: "text-emerald-500" },
    { key: "no_show", label: "No-show", value: k.no_show, icon: Ban, color: "text-amber-500" },
    { key: "aperte", label: "Trattative aperte", value: k.trattative_aperte, icon: Briefcase, color: "text-cyan-500", sub: fmtEuro(k.valore_aperto) },
    { key: "vinte", label: "Chiuse vinte", value: k.vinte, icon: Trophy, color: "text-emerald-500", sub: fmtEuro(k.valore_vinto) },
    { key: "perse", label: "Chiuse perse", value: k.perse, icon: XCircle, color: "text-red-500", sub: fmtEuro(k.valore_perso) },
    { key: "tasso", label: "Tasso di chiusura", value: `${k.tasso_chiusura}%`, icon: Percent, color: "text-violet-500", sub: "vinte / (vinte+perse)" },
    { key: "venduto", label: "Valore venduto", value: fmtEuro(k.valore_vinto), icon: Wallet, color: "text-yellow-500" },
  ];

  const tempData = Object.entries(data.by_temperature || {}).map(([kk, v]) => ({ name: TEMPERATURES[kk]?.label || kk, value: v, color: TEMPERATURES[kk]?.color || "#999" }));
  const statusData = Object.entries(data.by_status || {}).map(([kk, v]) => ({ name: kk, value: v }));
  const hasSales = (data.venduto?.ultime || []).length > 0 || data.venduto?.numero > 0;

  return (
    <div className="space-y-5">
      {/* 3. KPI principali */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpiCards.map((c) => (
          <div key={c.key} data-testid={`perf-kpi-${c.key}`} className="rounded-lg border bg-card p-4">
            <c.icon className={cn("w-5 h-5 mb-2", c.color)} />
            <div className="font-heading text-xl md:text-2xl font-black tabular-nums">{c.value}</div>
            <div className="text-xs text-muted-foreground">{c.label}</div>
            {c.sub && <div className="text-[11px] text-muted-foreground/70 mt-0.5">{c.sub}</div>}
          </div>
        ))}
      </div>

      {/* 4. Funnel + 8. Andamento */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Funnel commerciale" testId="perf-funnel">
          <Funnel steps={data.funnel} />
        </ChartCard>
        <ChartCard title="Andamento nel tempo" testId="perf-trend">
          {data.trend?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.trend} margin={{ left: -10, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" width={28} />
                <Tooltip contentStyle={TIP_STYLE} />
                <Line type="monotone" dataKey="assegnati" name="Assegnati" stroke="#3B82F6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="appuntamenti" name="Appuntamenti" stroke="#8B5CF6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="vinte" name="Vinte" stroke="#10B981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
      </div>

      {/* 5. Venduto */}
      <div className="rounded-lg border bg-card p-4" data-testid="perf-venduto">
        <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-emerald-500" /><h3 className="font-heading font-bold text-sm">Venduto</h3></div>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <MiniStat label="Totale venduto" value={fmtEuro(data.venduto.totale)} />
          <MiniStat label="Numero vendite" value={data.venduto.numero} />
          <MiniStat label="Valore medio vendita" value={fmtEuro(data.venduto.valore_medio)} />
        </div>
        {data.trend?.some((t) => t.valore_venduto > 0) ? (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data.trend} margin={{ left: -10, right: 8 }}>
              <defs>
                <linearGradient id="soldGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" width={44} tickFormatter={(v) => `€${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
              <Tooltip contentStyle={TIP_STYLE} formatter={(v) => fmtEuro(v)} />
              <Area type="monotone" dataKey="valore_venduto" name="Valore venduto" stroke="#10B981" strokeWidth={2} fill="url(#soldGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : <Empty />}

        {/* 11. Ultime chiuse vinte */}
        <div className="mt-4" data-testid="perf-ultime-vinte">
          <h4 className="font-heading font-bold text-xs uppercase tracking-wide text-muted-foreground mb-2">Ultime chiuse vinte</h4>
          {hasSales && data.venduto.ultime.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-xs text-muted-foreground text-left">
                  <th className="px-2 py-2 font-medium">Lead / Azienda</th>
                  <th className="px-2 py-2 font-medium">Servizio</th>
                  <th className="px-2 py-2 font-medium">Chiusura</th>
                  <th className="px-2 py-2 font-medium text-right">Valore</th>
                </tr></thead>
                <tbody>
                  {data.venduto.ultime.map((s) => (
                    <tr key={s.id} data-testid={`perf-sale-${s.id}`} onClick={() => navigate(`/leads/${s.id}`)}
                      className="border-b last:border-0 hover:bg-muted/40 cursor-pointer">
                      <td className="px-2 py-2.5"><div className="font-medium">{s.name}</div>{s.company && <div className="text-xs text-muted-foreground">{s.company}</div>}</td>
                      <td className="px-2 py-2.5 text-muted-foreground">{s.service || "—"}</td>
                      <td className="px-2 py-2.5 text-muted-foreground">{fmtDate(s.closed_at)}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">{fmtEuro(s.deal_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty />}
        </div>
      </div>

      {/* 6/7/8. Stati / Temperature / Interessi */}
      <div className="grid lg:grid-cols-3 gap-4">
        <ChartCard title="Lead per stato" testId="perf-status-chart">
          {statusData.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={statusData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={TIP_STYLE} />
                <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
        <ChartCard title="Lead per temperatura" testId="perf-temp-chart">
          {tempData.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={tempData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.value}`} labelLine={false} fontSize={11}>
                  {tempData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={TIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
        <ChartCard title="Servizi / Interessi" testId="perf-interest-chart">
          <MiniBars obj={data.by_interest} />
        </ChartCard>
      </div>

      {/* 9. Appuntamenti */}
      <div className="rounded-lg border bg-card p-4" data-testid="perf-appt">
        <div className="flex items-center gap-2 mb-3"><CalendarCheck className="w-4 h-4 text-indigo-500" /><h3 className="font-heading font-bold text-sm">Appuntamenti</h3></div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <MiniStat label="Fissati" value={data.appuntamenti.fissati} />
          <MiniStat label="Effettuati" value={data.appuntamenti.effettuati} />
          <MiniStat label="No-show" value={data.appuntamenti.no_show} />
          <MiniStat label="Annullati" value={data.appuntamenti.annullati} />
          <MiniStat label="Riprogrammati" value={data.appuntamenti.riprogrammati} />
        </div>
      </div>

      {/* 10. Follow-up e operatività */}
      <div className="rounded-lg border bg-card p-4">
        <h3 className="font-heading font-bold text-sm mb-3">Follow-up e operatività</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <OperCard testId="perf-oper-followup_oggi" label="Follow-up oggi" data={data.operativita.followup_oggi} navigate={navigate} color="text-indigo-500" />
          <OperCard testId="perf-oper-followup_scaduti" label="Follow-up scaduti" data={data.operativita.followup_scaduti} navigate={navigate} color="text-red-500" />
          <OperCard testId="perf-oper-followup_futuri" label="Follow-up futuri" data={data.operativita.followup_futuri} navigate={navigate} color="text-blue-500" />
          <OperCard testId="perf-oper-nuovi_da_contattare" label="Nuovi da contattare" data={data.operativita.nuovi_da_contattare} navigate={navigate} color="text-emerald-500" />
        </div>
      </div>
    </div>
  );
}

function Funnel({ steps }) {
  const max = Math.max(...steps.map((s) => s.value), 1);
  return (
    <div className="space-y-2.5 py-1">
      {steps.map((s, i) => {
        const prev = i > 0 ? steps[i - 1].value : null;
        const conv = prev ? Math.round((s.value / prev) * 100) : null;
        return (
          <div key={s.stage}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">{s.stage}</span>
              <span className="tabular-nums font-semibold">{s.value}{conv != null && <span className="text-muted-foreground font-normal ml-1.5">({conv}%)</span>}</span>
            </div>
            <div className="h-5 bg-muted rounded overflow-hidden">
              <div className="h-full bg-primary rounded transition-all" style={{ width: `${Math.max((s.value / max) * 100, s.value > 0 ? 4 : 0)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OperCard({ label, data, navigate, color, testId }) {
  const [open, setOpen] = useState(false);
  const has = data.count > 0;
  return (
    <div className="rounded-lg border bg-background" data-testid={testId}>
      <button onClick={() => has && setOpen((o) => !o)} className={cn("w-full text-left p-3", has && "hover:bg-accent transition-colors cursor-pointer")}>
        <div className={cn("font-heading text-2xl font-black tabular-nums", color)}>{data.count}</div>
        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
          {label}
          {has && <ChevronRight className={cn("w-3 h-3 transition-transform", open && "rotate-90")} />}
        </div>
      </button>
      {open && has && (
        <div className="border-t max-h-52 overflow-y-auto divide-y">
          {data.leads.map((l) => (
            <button key={l.id} data-testid={`perf-oper-lead-${l.id}`} onClick={() => navigate(`/leads/${l.id}`)}
              className="w-full text-left px-3 py-2 hover:bg-accent transition-colors">
              <div className="text-sm font-medium truncate">{l.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">{[l.company, l.status].filter(Boolean).join(" · ")}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return <div className="rounded-lg border bg-card p-4" data-testid={testId}><h3 className="font-heading font-bold text-sm mb-3">{title}</h3>{children}</div>;
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <div className="font-heading text-xl font-black tabular-nums">{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function MiniBars({ obj }) {
  const entries = Object.entries(obj || {}).filter(([k]) => k !== "None" && k !== "null").sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <Empty />;
  const max = Math.max(...entries.map((e) => e[1]), 1);
  return (
    <div className="space-y-2 py-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 text-xs">
          <span className="w-28 truncate text-muted-foreground">{k}</span>
          <div className="flex-1 h-4 bg-muted rounded overflow-hidden"><div className="h-full bg-primary rounded" style={{ width: `${(v / max) * 100}%` }} /></div>
          <span className="w-6 text-right tabular-nums">{v}</span>
        </div>
      ))}
    </div>
  );
}

function Empty() {
  return <div className="text-center text-sm text-muted-foreground py-10" data-testid="perf-empty">Nessun dato nel periodo selezionato</div>;
}
